const markets = [
  {
    id: 'automotive',
    label: 'Automotive',
    tag: 'Paint · Glass · Trim · Liquid PPF',
    body: 'Full ceramic coating systems for paint, glass, trim, and liquid PPF. From daily drivers to exotic vehicles and fleets.',
    image: 'https://public.readdy.ai/ai/img_res/7a4e848a71aa83c8c293a7a014c08649.jpg',
    icon: 'ri-car-fill',
  },
  {
    id: 'marine',
    label: 'Marine',
    tag: 'Boats · Yachts · Watercraft',
    body: 'Marine-grade ceramic formulations for vessels exposed to saltwater, UV, and sustained moisture. Permanent hull protection.',
    image: 'https://readdy.ai/api/search-image?query=luxury-yacht-ocean-water-marine-vessel-glossy-hull-surface-cinematic-blue-water-dramatic-sky-premium-photography&width=600&height=800&seq=market-marine-v3&orientation=portrait',
    icon: 'ri-ship-fill',
  },
  {
    id: 'aviation',
    label: 'Aviation',
    tag: 'Aircraft · Airframe Surfaces',
    body: 'High-performance coatings engineered for aviation requirements — permanent protection, UV resistance, and advanced surface adhesion.',
    image: 'https://readdy.ai/api/search-image?query=private-jet-aircraft-surface-protection-coating-runway-cinematic-aviation-close-up-detail-white-aircraft-dramatic-lighting-dark-sky&width=600&height=800&seq=market-avia-v3&orientation=portrait',
    icon: 'ri-flight-takeoff-fill',
  },
  {
    id: 'residential',
    label: 'Residential',
    tag: 'Tile · Stone · Glass · Facades',
    body: 'Ceramic protection adapted for home surfaces — countertops, tile, stone, glass, and exterior facades with hydrophobic performance.',
    image: 'https://readdy.ai/api/search-image?query=modern-luxury-home-kitchen-marble-countertops-interior-design-ceramic-protection-clean-surfaces-premium-residential-dark-moody-photography&width=600&height=800&seq=market-home-v3&orientation=portrait',
    icon: 'ri-home-5-fill',
  },
];

const MarketsSection = () => (
  <section id="markets" className="bg-[#070707] py-24 lg:py-32 border-t border-white/5">
    <div className="max-w-[1400px] mx-auto px-6 lg:px-14">

      {/* ── Header ── */}
      <div className="flex flex-col lg:flex-row lg:items-end justify-between gap-8 mb-14">
        <div>
          <div className="flex items-center gap-4 mb-5">
            <div className="h-px w-10 bg-[#c9a84c]" />
            <p className="text-[#c9a84c] text-[10px] tracking-[0.32em] font-black">APPLICATION MARKETS</p>
          </div>
          <h2
            className="text-white font-black leading-[0.95]"
            style={{ fontFamily: "'Space Grotesk', sans-serif", fontSize: 'clamp(34px, 4.8vw, 60px)', letterSpacing: '-0.03em' }}
          >
            Industries &amp;<br />
            <span style={{ WebkitTextStroke: '1.5px rgba(255,255,255,0.28)', color: 'transparent' }}>Surfaces We Serve</span>
          </h2>
        </div>
        <p className="text-white/35 text-[13px] max-w-[360px] leading-[1.85]">
          NanoPro® formulations are engineered across surface categories — one coating partner for every application environment.
        </p>
      </div>

      {/* ── Cinematic 4-col card grid ── */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
        {markets.map((market, idx) => (
          <div
            key={market.id}
            className="group relative overflow-hidden border border-white/5 hover:border-[#c9a84c]/22 transition-all duration-500 cursor-default"
            style={{ height: 'clamp(460px, 42vw, 580px)' }}
          >
            <img
              src={market.image}
              alt={`NanoPro ceramic coating for ${market.label}`}
              className="w-full h-full object-cover object-top group-hover:scale-[1.07] transition-transform duration-[900ms]"
            />

            {/* Two-layer gradient for depth */}
            <div className="absolute inset-0 bg-gradient-to-t from-[#030303]/99 via-[#030303]/55 to-[#030303]/15" />
            <div className="absolute inset-0 bg-gradient-to-b from-[#030303]/35 via-transparent to-transparent" />

            {/* Side number accent */}
            <div className="absolute top-6 right-6 flex items-center justify-center w-7 h-7 border border-white/12">
              <span className="text-white/28 text-[10px] font-black" style={{ fontFamily: "'Space Grotesk', sans-serif" }}>
                {String(idx + 1).padStart(2, '0')}
              </span>
            </div>

            {/* Content: icon top, text bottom */}
            <div className="absolute inset-0 flex flex-col justify-between p-7">
              {/* Top */}
              <div className="w-10 h-10 flex items-center justify-center border border-[#c9a84c]/20 bg-[#030303]/50 backdrop-blur-sm group-hover:border-[#c9a84c]/50 transition-all duration-300">
                <i className={`${market.icon} text-[#c9a84c] text-[18px]`} />
              </div>

              {/* Bottom */}
              <div>
                <div className="h-px w-6 bg-[#c9a84c]/50 mb-5 group-hover:w-14 transition-all duration-400" />
                <p className="text-[#c9a84c] text-[9px] tracking-[0.24em] font-black uppercase mb-3">{market.tag}</p>
                <h3
                  className="text-white font-black mb-3 leading-[1.05]"
                  style={{ fontFamily: "'Space Grotesk', sans-serif", fontSize: 'clamp(24px, 2.2vw, 30px)', letterSpacing: '-0.025em' }}
                >
                  {market.label}
                </h3>
                <p className="text-white/45 text-[12px] leading-[1.8]">{market.body}</p>
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* ── Specialty note ── */}
      <div className="mt-8 px-6 py-4 border border-white/5 bg-white/[0.018] flex flex-col sm:flex-row items-center justify-between gap-4">
        <div className="flex items-center gap-3">
          <i className="ri-star-line text-[#c9a84c] text-[14px]" />
          <p className="text-white/38 text-[12px]">Specialty surfaces also supported — wheels, leather, plastic trim, glass, residential facades, and more.</p>
        </div>
        <span className="text-white/18 text-[10px] tracking-[0.18em] whitespace-nowrap">ASK ABOUT SPECIALTY</span>
      </div>
    </div>
  </section>
);

export default MarketsSection;
